import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ReactHtmlParser from 'react-html-parser';
import { setUpdateInputParams, setDisplayInput } from '../../redux/modelDataSlice';

const InputModal = ({ isOpen, onClose, value, formattedName, unit, mappingName, simulationNumber }) => {
	const [inputValue, setInputValue] = useState(value);
	const [validationError, setValidationError] = useState('');
	const dispatch = useDispatch();
	const inputParameters = useSelector(
		state => state.designDefinition.partDefinition.simulations[simulationNumber].inputParameters
	);
	const displayInput = useSelector(state => state.modelData.displayInput);

	const parameterDefinition = inputParameters.find(param => param.mappingName === mappingName);

	useEffect(() => {
		if (isOpen) {
			setInputValue(value);
			setValidationError('');
		}
	}, [isOpen, value]);

	const handleChange = event => {
		const newValue = event.target.value;
		let regex;
		if (parameterDefinition?.isInteger) regex = /^\d*$/;
		else regex = /^-?\d*\.?\d{0,7}$/;
		if (regex.test(newValue)) {
			setInputValue(newValue);
			validateInput(newValue);
		}
	};

	const validateInput = newValue => {
		if (parameterDefinition) {
			const { minBoundary, maxBoundary } = parameterDefinition;
			const numericValue = parseFloat(newValue);
			if (numericValue < minBoundary || numericValue > maxBoundary) {
				setValidationError(`Value must be between ${minBoundary} and ${maxBoundary}`);
			} else if (isNaN(numericValue)) {
				setValidationError('Value cannot be empty or non-numeric');
			} else {
				setValidationError('');
			}
		}
	};

	const handleSave = () => {
		if (!validationError && inputValue !== null) {
			dispatch(setUpdateInputParams({ simulationNumber, mappingName, value: inputValue }));
			if (mappingName === displayInput[simulationNumber].mappingName) {
				dispatch(
					setDisplayInput({
						mappingName: mappingName,
						simNumber: simulationNumber,
						value: inputValue,
						unit: unit
					})
				);
			}
			onClose();
		}
	};

	const revertSVGStringFormat = str => {
		return str.replace(/<tspan dy="0.5em" font-size="0.7em">/g, '<sub>').replace(/<\/tspan>/g, '</sub>');
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Edit value</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						m: 'auto',
						width: 'fit-content'
					}}>
					<TextField
						autoFocus
						margin="dense"
						id="value"
						autoComplete="off"
						label={ReactHtmlParser(revertSVGStringFormat(formattedName)) || 'Value'}
						type="number"
						fullWidth
						variant="standard"
						value={inputValue}
						onChange={handleChange}
						InputProps={{
							endAdornment: <InputAdornment position="end">{unit}</InputAdornment>
						}}
						InputLabelProps={{
							style: {
								fontSize: '20px'
							},
							shrink: true
						}}
						error={!!validationError}
						helperText={validationError}
						onKeyDown={event => {
							if (parameterDefinition?.isInteger && ['-', '.'].includes(event.key)) {
								event.preventDefault();
							}
							if (event.key === 'Enter' && !validationError) {
								handleSave();
								event.preventDefault();
							}
						}}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button onClick={handleSave} disabled={!!validationError}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default InputModal;
